import { Button } from '@components/ui'
import { Row as TableRow } from '@tanstack/react-table'
import {
  commonActionColumn,
  renderGradeColumn,
  renderHeaderTooltip
} from '@utils/assessmentReportUtils'
import { Col, Flex, Progress, Row } from 'antd'
import { useState } from 'react'

import {
  Entities_Assessment_SkillSetsPercentAggregateByLevel_Details,
  Entities_Assessment_SkillSetsTeam_TeamSkillSet
} from '@suntex.fim/api-client'
import { skillSetGameDetails } from '@suntex.fim/instructions'
import { ColumnType, Modal, Tooltip } from '@suntex.fim/ui'

import {
  generateScrollableAssessmentCommonColumns,
  generateScrollableAssessmentCommonColumnsForSchool
} from './CommonColumns'

export type SkillSetsReportData =
  | Entities_Assessment_SkillSetsTeam_TeamSkillSet
  | Entities_Assessment_SkillSetsPercentAggregateByLevel_Details

/**
 * handles hidable columns list
 */
export const hidableSelectLabel: Record<string, string> = {
  userId: 'User ID',
  displayName: 'Display Name',
  stickers: 'Stickers',
  id: 'Id',
  userFirstName: 'First Name',
  userLastName: 'Last Name',
  estTimeOnTask: 'Est. Time on Task (H)',
  estProblemsSolved: 'Est. Problems Solved',
  lastActivity: 'Last Activity',
  SkillSet1: 'Skill Set 1',
  SkillSet2: 'Skill Set 2',
  SkillSet3: 'Skill Set 3',
  SkillSet4: 'Skill Set 4',
  SkillSet5: 'Skill Set 5',
  SkillSet6: 'Skill Set 6',
  SkillSet7: 'Skill Set 7',
  SkillSet8: 'Skill Set 8'
}

export const schoolHidableSelectLabel: Record<string, string> = {
  name: 'Name',
  desc: 'Class Name',
  contactName: 'Teacher',
  grade: 'Grade',
  students: 'Students',
  stickers: 'Stickers',
  ...Object.fromEntries(Object.entries(hidableSelectLabel).slice(-8))
}

function SkillSetHeading({ index }: { index: number }) {
  const [skillSetModal, setSkillSetModal] = useState(false)

  const details = skillSetGameDetails[index]
  return (
    <>
      <Button
        intent="icon"
        className="text-white assessment-report-wrapped-header"
        onClick={() => setSkillSetModal(true)}
      >
        {renderHeaderTooltip(`Skill Set ${index + 1} `)}
      </Button>
      <Modal
        closable={false}
        centered
        title={`Skill Set ${index + 1}`}
        open={skillSetModal}
        onOk={() => setSkillSetModal(false)}
        onCancel={() => setSkillSetModal(false)}
        width={900}
        className="skillset-modal"
        footer={[
          <Button
            shape="round"
            intent="tertiary"
            key="submit"
            onClick={() => setSkillSetModal(false)}
          >
            Okay
          </Button>
        ]}
      >
        <p className="fw-bold">{details?.text}</p>
        <Row gutter={[20, 20]}>
          {details?.games.map(
            (
              {
                gameName,
                gameDescriptions,
                challenge,
                rule,
                example,
                image
              }: any,
              gameIndex: number
            ) => (
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              <Col key={gameName} span={24}>
                <Flex className="card" gap="24px" align="center">
                  <Flex gap="14px" vertical>
                    <div>
                      <p className="fw-bold">
                        Game {gameIndex + 1}:{' '}
                        <span className="text-primary">{gameName}</span>
                      </p>
                      {gameDescriptions.map((item: string) => (
                        <p key={item} className="text-sm">
                          {item}
                        </p>
                      ))}
                    </div>
                    <p className="fw-bold">{challenge}</p>
                    <p>{rule}</p>
                    <div>
                      <p className="fw-bold">Example:</p>
                      {example.map((item: string) => (
                        <p key={item}>{item}</p>
                      ))}
                    </div>
                  </Flex>
                  <img
                    className="card-image"
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    src={image}
                    alt="Modal Game img"
                  />
                </Flex>
              </Col>
            )
          )}
        </Row>
      </Modal>
    </>
  )
}

/**
 * returns JSX for skill set games report table
 */
function columns({
  graphColor,
  sendMessage
}: {
  graphColor: string
  sendMessage?: (t: TableRow<SkillSetsReportData>[]) => void
}) {
  /**
   * renders progress bar in table cell
   */
  const skillSetColumns: ColumnType<SkillSetsReportData>[] = [
    ...Array(8).keys()
  ].map((i) => ({
    header: () => <SkillSetHeading index={i} />,
    accessorFn: ({ ss }) => ss?.[i + 1] ?? 0,
    id: `SkillSet${i + 1}`,
    size: 150,
    cell: (info) => (
      <Tooltip
        title={`${Number(info.getValue()) || 0}% complete`}
        classNames={{ root: 'progress-tooltip' }}
        placement="right"
        arrow={false}
      >
        <Progress
          strokeColor={graphColor}
          percent={Number(info.getValue()) || 0}
          size={['100%', 32]}
          showInfo={false}
        />
      </Tooltip>
    )
  }))
  const schoolCommonColumns =
    generateScrollableAssessmentCommonColumnsForSchool<SkillSetsReportData>()
  let actioncolumn: any = {}
  let finalColumns: ColumnType<SkillSetsReportData>[] = []
  if (sendMessage) {
    actioncolumn = commonActionColumn<SkillSetsReportData>(
      hidableSelectLabel,
      sendMessage
    )
  }

  /**
   * handles table column mapping
   */
  if (sendMessage) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    finalColumns = [
      actioncolumn,
      ...generateScrollableAssessmentCommonColumns<SkillSetsReportData>(),
      ...skillSetColumns
    ]
  } else {
    finalColumns = [
      ...schoolCommonColumns.slice(0, 3),
      {
        header: () => renderHeaderTooltip('Grade'),
        size: 110,
        accessorKey: 'grade',
        cell: (info) => {
          return renderGradeColumn(info.getValue())
        },
        id: 'grade',
        enableSorting: true
      },
      ...schoolCommonColumns.slice(4),
      ...skillSetColumns
    ]
  }

  return finalColumns
}

export default columns
